import { inferBrowser } from '@modules/browser';
import { isIOS } from '@modules/document-helpers';
import { useEffect } from 'react';

import { type CardFlavour, CardFlavours } from '../types';
import { CardLayout, type ImageFieldId, type ImageType, type ShowPopover } from './types';
import type { CardImage, ValueProvider } from '.cache/__types__';

export const WEB_EDITOR_VERSION = 'web_editor_v1';

export function assertIsCardLayout(
  value?: string | CardLayout | null,
): asserts value is CardLayout | undefined | null {
  if (!value) {
    return;
  }
  if (!Object.values(CardLayout).includes(value as CardLayout)) {
    throw new Error(`Value "${value}" is not a member of the CardLayout enum.`);
  }
}

export function toFriendlyName(imgType: ImageType): string {
  switch (imgType) {
    case 'cover':
      return 'Cover Photo';
    case 'logo':
      return 'Company Logo';
    case 'profile':
      return 'Profile Picture';
  }
}

/** Returns the field ID for the image type. Used to identify locked fields. */
export const typeToFieldId = (type: ImageType): ImageFieldId => {
  switch (type) {
    case 'profile':
      return 'profile_picture';
    case 'cover':
      return 'cover_photo';
    case 'logo':
      return 'company_logo';
  }
};

export const cardImageAttributeNameToImageType = (name?: string | null): ImageType => {
  switch (name) {
    case 'company_logo':
      return 'logo';
    case 'profile_picture':
      return 'profile';
    case 'cover_photo':
    default:
      return 'cover';
  }
};

/** Calculate the image type from the popover information */
export const defaultTypeFromPopover = (p?: ShowPopover, layout?: CardLayout): ImageType => {
  if (p?.type) {
    return p.type;
  }
  if (!p?.position) {
    return 'cover';
  }

  const locations: { left: ImageType; right: ImageType; banner: ImageType } = {
    left: 'profile',
    right: 'logo',
    banner: 'cover',
  };
  if (layout) {
    const locationsFromLayout = imageLocationsFromLayout(layout);
    locations.banner = locationsFromLayout.banner || locations.banner;
    locations.left = locationsFromLayout.left || locations.left;
    locations.right = locationsFromLayout.right || locations.right;
  }

  return locations[p.position];
};

export type Locations = { banner?: ImageType; left?: ImageType; right?: ImageType };

export const imageLocationsFromLayout = (
  layout: CardLayout,
  previews?: { left: boolean; right: boolean } | undefined,
): Locations => {
  const result: Locations = {};
  switch (layout) {
    case '1A':
      result.banner = 'profile';
      break;
    case '1B':
      result.banner = 'logo';
      break;
    case '1C':
      result.banner = 'cover';
      break;
    case '2A':
      result.banner = 'profile';
      result.left = 'logo';
      break;
    case '2B':
      result.banner = 'logo';
      result.left = 'profile';
      break;
    case '2C':
      result.banner = 'cover';
      result.left = 'profile';
      break;
    case '2D':
      result.banner = 'cover';
      result.left = 'logo';
      break;
    case '3A':
      result.banner = 'cover';
      result.right = 'logo';
      result.left = 'profile';
  }
  if (previews?.left && result.banner !== 'profile') {
    result.left ??= 'profile';
  }
  if (previews?.right && result.banner !== 'logo') {
    result.right ??= 'logo';
  }
  // default case
  return result;
};

export function allLayouts(): CardLayout[] {
  return [
    CardLayout.Layout_1A,
    CardLayout.Layout_1B,
    CardLayout.Layout_1C,
    CardLayout.Layout_2A,
    CardLayout.Layout_2B,
    CardLayout.Layout_2C,
    CardLayout.Layout_2D,
    CardLayout.Layout_3A,
  ];
}

export const hasLayouts = ({
  cover,
  logo,
  profile,
}: {
  cover?: boolean | string | null;
  logo?: boolean | string | null;
  profile?: boolean | string | null;
}): CardLayout[] => {
  const layouts: CardLayout[] = [];
  if (profile) layouts.push(CardLayout.Layout_1A);
  if (logo) layouts.push(CardLayout.Layout_1B);
  if (cover) layouts.push(CardLayout.Layout_1C);

  if (profile && logo) {
    layouts.push(CardLayout.Layout_2A);
    layouts.push(CardLayout.Layout_2B);
  }
  if (profile && cover) layouts.push(CardLayout.Layout_2C);
  if (cover && logo) layouts.push(CardLayout.Layout_2D);
  if (profile && logo && cover) layouts.push(CardLayout.Layout_3A);

  return layouts;
};

export const canUseLayout = (
  p: {
    cover?: boolean | string | null;
    logo?: boolean | string | null;
    profile?: boolean | string | null;
  },
  layout: CardLayout,
): boolean => {
  const layouts = hasLayouts(p);
  return !!layouts.find((_) => _ === layout);
};

/** This is basically an overload of getSuggestedLayout which lets you provide the whole
 * CardImage from the graph and tells you which layout you should use.
 */
export const getSuggestedLayoutFromCardImages = (p: {
  cover?: Partial<CardImage> | null | undefined;
  logo?: Partial<CardImage> | null | undefined;
  profile?: Partial<CardImage> | null | undefined;
  type: ImageType | undefined;
}): CardLayout | undefined => {
  const hasCover = !!p.cover?.edited_url || p.type === 'cover' || !!p.cover?.value_provider;
  const hasProfile = !!p.profile?.edited_url || p.type === 'profile' || !!p.profile?.value_provider;
  const hasLogo = !!p.logo?.edited_url || p.type === 'logo' || !!p.logo?.value_provider;

  return getSuggestedLayout({ cover: hasCover, logo: hasLogo, profile: hasProfile });
};

export const getSuggestedLayout = (p: {
  cover?: boolean | string | null;
  logo?: boolean | string | null;
  profile?: boolean | string | null;
}): CardLayout | undefined => {
  if (p.cover && p.logo && p.profile) {
    return CardLayout.Layout_3A;
  } else if (p.cover && p.logo) {
    return CardLayout.Layout_2D;
  } else if (p.cover && p.profile) {
    return CardLayout.Layout_2C;
  } else if (p.logo && p.profile) {
    return CardLayout.Layout_2B;
  } else if (p.cover) {
    return CardLayout.Layout_1C;
  } else if (p.profile) {
    return CardLayout.Layout_1A;
  } else if (p.logo) {
    return CardLayout.Layout_1B;
  } else {
    return undefined;
  }
};

export const getImagesRequiresForLayout = (
  p: {
    cover?: boolean | string | null;
    logo?: boolean | string | null;
    profile?: boolean | string | null;
  },
  layout: CardLayout,
): ImageType[] => {
  const requiredImages: ImageType[] = [];
  if (canUseLayout(p, layout)) {
    return requiredImages;
  }

  switch (layout) {
    case '1A': {
      requiredImages.push('profile');
      break;
    }
    case '1B': {
      requiredImages.push('logo');
      break;
    }
    case '1C': {
      requiredImages.push('cover');
      break;
    }
    case '2B':
    case '2A': {
      if (!p.profile) requiredImages.push('profile');
      if (!p.logo) requiredImages.push('logo');
      break;
    }
    case '2C': {
      if (!p.profile) requiredImages.push('profile');
      if (!p.cover) requiredImages.push('cover');
      break;
    }
    case '2D': {
      if (!p.logo) requiredImages.push('logo');
      if (!p.cover) requiredImages.push('cover');
      break;
    }
    case '3A': {
      if (!p.profile) requiredImages.push('profile');
      if (!p.logo) requiredImages.push('logo');
      if (!p.cover) requiredImages.push('cover');
      break;
    }
  }
  return [...new Set(requiredImages)];
};

/**
 * useDisableIOSPullToRefresh
 *
 * This hook is a dirty hack.
 *
 * We use it to set 'overflow: hidden' on the document body
 * if the current OS is iOS & the browser is Safari.
 *
 * This disables the pull to refresh behavior on iOS Safari,
 * so that users can tap and move around their finger in our color picker
 * without things sliding around weirdly.
 *
 * This hook is only be used in the context of the image edit popover
 * where the color picker shows up.
 */
export function useDisableIOSPullToRefresh() {
  useEffect(() => {
    const safari = inferBrowser();
    const iOS = isIOS();

    if (safari && iOS) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'visible';
      };
    }
  });
}

export const getDefaultValueProviderForCardImage = (
  flavour: CardFlavour | undefined,
  type: ImageType,
  image: Partial<CardImage> | undefined,
): ValueProvider | undefined => {
  switch (flavour) {
    case CardFlavours.TEMPLATE: {
      if (image?.edited_url) {
        return 'template';
      }
      return type === 'profile' ? 'member' : 'template';
    }
    default: {
      if (image?.edited_url) {
        return 'admin';
      }
      return type === 'profile' ? 'member' : 'admin';
    }
  }
};

export const shouldUseSuggestedLayout = (currentLayout: CardLayout, suggestedLayout: CardLayout): boolean => {
  if (currentLayout === CardLayout.Layout_2A && suggestedLayout === CardLayout.Layout_2B) {
    return false;
  }
  return true;
};
